import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Loader2, Package, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/shared/PageHeader";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { TableCard } from "@/components/shared/TableCard";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { cn } from "@/lib/utils";
import { productKeys } from "@/lib/queryKeys";
import {
  findTaxOptionByCategoryAndRate,
  taxOptionsForRegime,
} from "@/lib/taxOptions";
import { useTenantSpecialRegime } from "@/lib/useTenantSpecialRegime";
import {
  createProduct,
  deleteProduct,
  listProducts,
  updateProduct,
  type Product,
} from "@/services/productService";

/**
 * Company-level product catalog.
 *
 * The per-client catalog (ClientCatalogTab) only surfaces once a client is
 * selected, so a consumidor-final invoice — the most common kind — had no list
 * to pick from at all. These products appear on every invoice line.
 *
 * Deliberately the same columns and the same dialog shape as the client
 * catalog: the two feed one picker, and teaching two different interactions
 * for the same job is how they drift apart.
 */

interface FormState {
  sku: string;
  description: string;
  unitPrice: string;
  /** The OPTION id, not the category — 2,2% and 2,75% are both vat_other. */
  taxOptionId: string;
}

const EMPTY_FORM: FormState = {
  sku: "",
  description: "",
  unitPrice: "",
  taxOptionId: "vat_basic",
};

export function ProductsPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  // Régimen-aware: an automotora may catalogue a vehicle at 2,2%.
  const specialRegime = useTenantSpecialRegime();
  const TAX_OPTIONS = taxOptionsForRegime(specialRegime);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const { data: products = [], isLoading } = useQuery({
    queryKey: productKeys.list(),
    queryFn: listProducts,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: productKeys.all });
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        sku: form.sku.trim() || undefined,
        description: form.description.trim(),
        unitPrice: Number(form.unitPrice),
        defaultTaxCategory: selectedOption.category,
        defaultTaxRate: selectedOption.rate,
      };
      return editing
        ? updateProduct(editing.id, { ...payload, sku: payload.sku ?? null })
        : createProduct(payload);
    },
    onSuccess: async () => {
      await invalidate();
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      toast.success(t("products.saved", "Producto guardado."));
    },
    onError: () =>
      toast.error(t("products.saveError", "No pudimos guardar el producto.")),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: async () => {
      await invalidate();
      setDeleteTarget(null);
      toast.success(t("products.deleted", "Producto eliminado."));
    },
    onError: () =>
      toast.error(t("products.deleteError", "No pudimos eliminar el producto.")),
  });

  const openNew = (): void => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (p: Product): void => {
    setEditing(p);
    setForm({
      sku: p.sku ?? "",
      description: p.description,
      unitPrice: String(p.unitPrice),
      taxOptionId:
        findTaxOptionByCategoryAndRate(
          p.defaultTaxCategory as never,
          p.defaultTaxRate,
        )?.id ?? "vat_basic",
    });
    setDialogOpen(true);
  };

  const formatPrice = (v: number): string =>
    v.toLocaleString("es-UY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const selectedOption =
    TAX_OPTIONS.find((o) => o.id === form.taxOptionId) ?? TAX_OPTIONS[0];

  const taxLabel = (category: string): string => {
    const opt = TAX_OPTIONS.find((o) => o.category === category);
    return opt ? t(opt.labelKey, opt.fallback) : category;
  };

  /** Same action menu the other grids use, so edit/delete sit where the rest
   *  of the app puts them — and so the mobile card and the table row cannot
   *  drift apart. */
  const rowActions = (p: Product): TableAction[] => [
    {
      label: t("common.edit", "Editar"),
      icon: Pencil,
      onClick: () => openEdit(p),
    },
    {
      label: t("common.delete", "Eliminar"),
      icon: Trash2,
      variant: "destructive",
      onClick: () => setDeleteTarget(p),
      disabled: remove.isPending,
    },
  ];

  const price = Number(form.unitPrice);
  const canSave =
    form.description.trim().length > 0 &&
    form.unitPrice.trim() !== "" &&
    Number.isFinite(price) &&
    price >= 0 &&
    !save.isPending;

  return (
    <div className="fx-content space-y-4">
      <PageHeader
        title={t("products.title", "Productos")}
        description={t(
          "products.description",
          "Tu lista de productos y servicios. Aparecen al crear una factura, con su precio y su tasa de IVA.",
        )}
        actions={
          <Button type="button" onClick={openNew}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            {t("products.new", "Nuevo producto")}
          </Button>
        }
      />

      {/* TableCard + LoadingLogo + EmptyState + a mobile card list is the
          house pattern for every list in the portal (see clients-page). A
          bespoke table here would be the one grid that looks and behaves
          differently, and mobile parity is a project rule. */}
      <TableCard>
        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : products.length === 0 ? (
          <EmptyState
            icon={<Package />}
            title={t("products.empty", "Todavía no cargaste productos.")}
            message={t(
              "products.emptyHint",
              "Agregá el primero para elegirlo al facturar, con su precio y su tasa de IVA.",
            )}
            action={
              <Button type="button" onClick={openNew}>
                <Plus className="mr-2 h-4 w-4" aria-hidden />
                {t("products.new", "Nuevo producto")}
              </Button>
            }
          />
        ) : (
          <>
            <div className="space-y-3 py-2 md:hidden">
              {products.map((p) => (
                <Card
                  key={p.id}
                  className={cn(
                    "overflow-hidden border-border p-4 text-left transition-colors duration-150",
                    "hover:bg-muted/30",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="font-medium text-foreground">
                        {p.description}
                      </p>
                      {p.sku?.trim() ? (
                        <p className="font-mono text-xs text-muted-foreground">
                          {p.sku.trim()}
                        </p>
                      ) : null}
                      <p className="text-sm tabular-nums text-foreground">
                        {formatPrice(p.unitPrice)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {taxLabel(p.defaultTaxCategory)}
                      </p>
                    </div>
                    <div className="shrink-0" data-interactive>
                      <TableActionsMenu actions={rowActions(p)} />
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("products.col.sku", "Código")}</TableHead>
                    <TableHead>
                      {t("products.col.description", "Descripción")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("products.col.price", "Precio sin IVA")}
                    </TableHead>
                    <TableHead>{t("products.col.tax", "Tasa IVA")}</TableHead>
                    <TableHead className="w-[60px]" aria-hidden />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono text-xs">
                        {p.sku?.trim() || "—"}
                      </TableCell>
                      <TableCell>{p.description}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPrice(p.unitPrice)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {taxLabel(p.defaultTaxCategory)}
                      </TableCell>
                      <TableCell>
                        <TableActionsMenu actions={rowActions(p)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </TableCard>

      <ConfirmationDialog
        open={deleteTarget != null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("products.deleteConfirmTitle", "Eliminar producto")}
        description={t(
          "products.deleteConfirmDescription",
          "Deja de ofrecerse al facturar. Las facturas ya emitidas no cambian.",
        )}
        confirmText={t("common.delete", "Eliminar")}
        cancelText={t("common.cancel", "Cancelar")}
        variant="destructive"
        isLoading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t("products.editTitle", "Editar producto")
                : t("products.newTitle", "Nuevo producto")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="product-sku">
                {t("products.form.sku", "Código (opcional)")}
              </Label>
              <Input
                id="product-sku"
                value={form.sku}
                maxLength={64}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-description">
                {t("products.form.description", "Descripción")}
              </Label>
              <Input
                id="product-description"
                value={form.description}
                maxLength={500}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-price">
                {t("products.form.price", "Precio sin IVA")}
              </Label>
              <Input
                id="product-price"
                inputMode="decimal"
                value={form.unitPrice}
                onChange={(e) =>
                  setForm({ ...form, unitPrice: e.target.value })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.form.tax", "Tasa IVA")}</Label>
              <Select
                value={form.taxOptionId}
                onValueChange={(v) => setForm({ ...form, taxOptionId: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAX_OPTIONS.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {t(o.labelKey, o.fallback)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button
              type="button"
              disabled={!canSave}
              onClick={() => save.mutate()}
            >
              {save.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              ) : null}
              {t("common.save", "Guardar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
